import React, { useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from 'cpool-components/general/Popover';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { CurrencyInput } from 'cpool-components/input/CurrencyInput';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS, type LOAN_REQUEST_OFFER_SOURCE } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { LuCircleX, LuPencil, LuSave } from 'react-icons/lu';

import { OfferTableCell } from './OfferTableCell';
import { useOfferCommissionMutation } from '../../hooks/useOfferCommissionMutation';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { calculateCommission } from '~/features/leadDetails/utils/calculateCommission';
import { currencyFormat, numberFormat } from '~/lib/i18n';

type Props = {
    source: LOAN_REQUEST_OFFER_SOURCE;
    id: string;
    leadId: string;
    receivedCommission?: number | null;
    responseCommissionInPercent: number | null;
    kreditbetrag?: number | null;
};

// Shows expected commission, then received-with-expected-subline once recorded, via an in-cell popover editor.
export const OfferCommission: React.FC<Props> = ({
    id,
    kreditbetrag,
    leadId,
    receivedCommission,
    responseCommissionInPercent,
    source,
}) => {
    const { t } = useTranslation();
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [commission, setCommission] = useState<number | null>(receivedCommission ?? null);
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const queryMutator = useOfferCommissionMutation({ id, leadId, source });
    const canChangeCommission =
        permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST][LOAN_REQUEST_COMMANDS.changeOfferCommission];

    const expectedCommission = calculateCommission({ kreditbetrag, provision: responseCommissionInPercent });
    const expectedCommissionLabel = expectedCommission ? currencyFormat(expectedCommission) : null;
    const hasReceivedCommission = receivedCommission !== null && receivedCommission !== undefined;

    const saveCommission = () => {
        setIsEditOpen(false);
        if (commission === receivedCommission) return;
        queryMutator.mutate(commission ?? 0);
    };

    const cancelEdit = () => {
        setCommission(receivedCommission ?? null);
        setIsEditOpen(false);
    };

    const renderAmount = () => {
        if (hasReceivedCommission) return currencyFormat(receivedCommission);
        if (expectedCommissionLabel)
            return t('OFFER_MANAGEMENT.COMMISSION.EXPECTED_VALUE', {
                commission: expectedCommissionLabel,
                percent: numberFormat(responseCommissionInPercent ?? 0),
            });
        return '-';
    };

    return (
        <OfferTableCell>
            <div className="flex items-center gap-2">
                <div className="flex flex-col">
                    <span>{renderAmount()}</span>
                    <span className="text-12 text-grey">
                        {hasReceivedCommission
                            ? t('OFFER_MANAGEMENT.COMMISSION.EXPECTED_WAS', {
                                  commission:
                                      expectedCommissionLabel ??
                                      t('OFFER_MANAGEMENT.COMMISSION.EXPECTED_NOT_AVAILABLE'),
                              })
                            : t('OFFER_MANAGEMENT.COMMISSION.EXPECTED')}
                    </span>
                </div>
                {canChangeCommission && (
                    <Popover open={isEditOpen} onOpenChange={(open) => (open ? setIsEditOpen(true) : cancelEdit())}>
                        <PopoverTrigger asChild>
                            <button
                                aria-label={t('OFFER_MANAGEMENT.COMMISSION.EDIT_RECEIVED')}
                                className="rounded p-1.5 text-midnight transition-colors hover:bg-surface-light"
                                data-testid={`edit-received-commission-${id}`}
                                onClick={(event) => event.stopPropagation()}
                                type="button"
                            >
                                <LuPencil size={16} />
                            </button>
                        </PopoverTrigger>
                        <PopoverContent
                            align="start"
                            className="flex w-64 flex-col gap-4"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <span className="text-14 font-bold text-midnight">
                                {t('OFFER_MANAGEMENT.COMMISSION.EDIT_TITLE')}
                            </span>
                            <CurrencyInput
                                id={`received-commission-${id}`}
                                onChange={setCommission}
                                value={commission}
                            />
                            <div className="flex items-center justify-end gap-2">
                                <Button
                                    iconLeft={<LuCircleX className="h-4 w-4" />}
                                    onClick={cancelEdit}
                                    type="button"
                                    variant={ButtonVariant.SECONDARY}
                                >
                                    {t('BUTTONS.CANCEL')}
                                </Button>
                                <Button
                                    iconLeft={<LuSave className="h-4 w-4" />}
                                    onClick={saveCommission}
                                    type="button"
                                >
                                    {t('BUTTONS.SAVE')}
                                </Button>
                            </div>
                        </PopoverContent>
                    </Popover>
                )}
            </div>
        </OfferTableCell>
    );
};
